import { SupabaseClient } from "@supabase/supabase-js";
import { RoomRepository } from "../repositories/room.repository";
import { ProjectUserRepository } from "../repositories/project-user.repository";
import {
  PaginationOptions,
  PaginatedResponse,
} from "../types/pagination.types";
import {
  CreateRoomDTO,
  RoomDTO,
  RoomMapper,
  UpdateRoomDTO,
} from "../dtos/room.dto";

export class RoomService {
  private roomRepository: RoomRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(private supabase: SupabaseClient) {
    this.roomRepository = new RoomRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * Create a new room
   */
  async createRoom(dto: CreateRoomDTO, userId: string): Promise<RoomDTO> {
    // Check if user has access to the project
    const projectUser = await this.projectUserRepository.findByProjectAndUser(
      dto.projectId,
      userId,
    );
    if (!projectUser) {
      throw new Error("User is not a member of this project");
    }

    // Create the room
    const room = await this.roomRepository.create({
      user_id: userId,
      project_id: dto.projectId,
      name: dto.name,
      description: dto.description || null,
    });

    return RoomMapper.toDTO(room);
  }

  /**
   * Get a room by ID
   */
  async getRoom(roomId: string, userId: string): Promise<RoomDTO> {
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    // Check if user has access to the room
    if (room.user_id !== userId) {
      // Check if user is a member of the project
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    return RoomMapper.toDTO(room);
  }

  /**
   * Update room information
   */
  async updateRoom(
    roomId: string,
    dto: UpdateRoomDTO,
    userId: string,
  ): Promise<RoomDTO> {
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    // Only room owner can update
    if (room.user_id !== userId) {
      throw new Error("Only room owner can update room information");
    }

    // Apply updates using entity methods
    if (dto.name !== undefined || dto.description !== undefined) {
      room.updateInfo(
        dto.name || room.name,
        dto.description !== undefined ? dto.description : room.description,
      );
    }

    // Save changes
    const updatedRoom = await this.roomRepository.save(room);
    return RoomMapper.toDTO(updatedRoom);
  }

  /**
   * Delete a room
   */
  async deleteRoom(roomId: string, userId: string): Promise<void> {
    const room = await this.roomRepository.findById(roomId);
    if (!room) {
      throw new Error("Room not found");
    }

    // Only room owner can delete
    if (room.user_id !== userId) {
      throw new Error("Only room owner can delete the room");
    }

    await this.roomRepository.delete(roomId);
  }

  /**
   * Get user's rooms
   */
  async getUserRooms(userId: string): Promise<RoomDTO[]> {
    const rooms = await this.roomRepository.findByUser(userId);
    return rooms.map((room) => RoomMapper.toDTO(room));
  }

  /**
   * Get user's rooms (paginated)
   */
  async getUserRoomsPaginated(
    userId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<RoomDTO>> {
    const result = await this.roomRepository.findByUserPaginated(
      userId,
      options,
    );
    return {
      data: result.data.map((room) => RoomMapper.toDTO(room)),
      pagination: result.pagination,
    };
  }

  /**
   * Get project's rooms
   */
  async getProjectRooms(projectId: string, userId: string): Promise<RoomDTO[]> {
    // Check if user has access to the project
    const projectUser = await this.projectUserRepository.findByProjectAndUser(
      projectId,
      userId,
    );
    if (!projectUser) {
      throw new Error("User is not a member of this project");
    }

    const rooms = await this.roomRepository.findByProject(projectId);
    return rooms.map((room) => RoomMapper.toDTO(room));
  }

  /**
   * Get project's rooms (paginated)
   */
  async getProjectRoomsPaginated(
    projectId: string,
    userId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<RoomDTO>> {
    // Check if user has access to the project
    const projectUser = await this.projectUserRepository.findByProjectAndUser(
      projectId,
      userId,
    );
    if (!projectUser) {
      throw new Error("User is not a member of this project");
    }

    const result = await this.roomRepository.findByProjectPaginated(
      projectId,
      options,
    );
    return {
      data: result.data.map((room) => RoomMapper.toDTO(room)),
      pagination: result.pagination,
    };
  }

  /**
   * Get user's rooms for a specific project
   */
  async getUserProjectRooms(
    projectId: string,
    userId: string,
  ): Promise<RoomDTO[]> {
    // Check if user has access to the project
    const projectUser = await this.projectUserRepository.findByProjectAndUser(
      projectId,
      userId,
    );
    if (!projectUser) {
      throw new Error("User is not a member of this project");
    }

    const rooms = await this.roomRepository.findByUserAndProject(
      userId,
      projectId,
    );
    return rooms.map((room) => RoomMapper.toDTO(room));
  }

  /**
   * Get user's rooms for a specific project (paginated)
   */
  async getUserProjectRoomsPaginated(
    projectId: string,
    userId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<RoomDTO>> {
    // Check if user has access to the project
    const projectUser = await this.projectUserRepository.findByProjectAndUser(
      projectId,
      userId,
    );
    if (!projectUser) {
      throw new Error("User is not a member of this project");
    }

    const result = await this.roomRepository.findByUserAndProjectPaginated(
      userId,
      projectId,
      options,
    );
    return {
      data: result.data.map((room) => RoomMapper.toDTO(room)),
      pagination: result.pagination,
    };
  }

  /**
   * Search rooms by name
   */
  async searchRooms(searchTerm: string, userId: string): Promise<RoomDTO[]> {
    const rooms = await this.roomRepository.searchByName(userId, searchTerm);
    return rooms.map((room) => RoomMapper.toDTO(room));
  }

  /**
   * Search rooms by name (paginated)
   */
  async searchRoomsPaginated(
    searchTerm: string,
    userId: string,
    options: PaginationOptions,
  ): Promise<PaginatedResponse<RoomDTO>> {
    const result = await this.roomRepository.searchByNamePaginated(
      userId,
      searchTerm,
      options,
    );
    return {
      data: result.data.map((room) => RoomMapper.toDTO(room)),
      pagination: result.pagination,
    };
  }

  /**
   * Get room with latest chat preview
   */
  async getRoomWithLatestChat(
    roomId: string,
    userId: string,
  ): Promise<{ room: RoomDTO; latestChat: any }> {
    const result = await this.roomRepository.findWithLatestChat(roomId);
    if (!result) {
      throw new Error("Room not found");
    }

    // Check if user has access
    if (result.room.user_id !== userId) {
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        result.room.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("User does not have access to this room");
      }
    }

    return {
      room: RoomMapper.toDTO(result.room),
      latestChat: result.latestChat,
    };
  }
}
